import { useCallback, useEffect, useRef, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { TRANSACTION_ID_PARAM } from "../constants";

import type { FilterStorage } from "../context/FilterContext";

type Edge = "first" | "last";

type EdgeIntent<T> = {
  edge: Edge;
  page: number;
  rowsAtIntent: T[];
  /** The page we left, so a failed crossing can go back to it. */
  fromPage: number;
};

export interface UseTransactionPanelSelectionOptions<T> {
  rows: T[];
  getRowId: (row: T) => string;
  storage?: FilterStorage;
  isLoading?: boolean;
  /** True while a page is in flight. Distinct from `isLoading`, which stays false on a refetch. */
  isFetching?: boolean;
  error?: string | null;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  /** Skips the stale-id cleanup so a caller resolving the id another way (e.g. a fallback fetch) can keep the url as the source of truth for a shareable link. */
  keepUrlParam?: boolean;
}

export interface UseTransactionPanelSelectionResult<T> {
  selectedRow: T | null;
  openPanel: (row: T) => void;
  closePanel: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  /** True while an adjacent page is being fetched to continue navigation. */
  isCrossingPage: boolean;
}

export const useTransactionPanelSelection = <T>({
  rows,
  getRowId,
  storage = "url",
  isLoading = false,
  isFetching = false,
  error = null,
  page = 1,
  totalPages = 1,
  onPageChange,
  keepUrlParam = false,
}: UseTransactionPanelSelectionOptions<T>): UseTransactionPanelSelectionResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);
  const [edgeIntent, setEdgeIntent] = useState<EdgeIntent<T> | null>(null);
  const selectedId =
    storage === "local"
      ? localSelectedId
      : searchParams.get(TRANSACTION_ID_PARAM);
  const selectedIndex =
    selectedId === null
      ? -1
      : rows.findIndex((row) => getRowId(row) === selectedId);

  const setSelectedId = useCallback(
    (id: string | null, options?: { replace?: boolean }) => {
      if (storage === "local") {
        setLocalSelectedId(id);
        return;
      }
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (id === null) {
          next.delete(TRANSACTION_ID_PARAM);
        } else {
          next.set(TRANSACTION_ID_PARAM, id);
        }
        return next;
      }, options);
    },
    [storage, setSearchParams]
  );

  // Skip cleanup while loading so a deep-linked id survives the initial fetch, and while
  // crossing a page boundary: the url still holds the previous page's id until the new
  // page arrives, which is exactly the "id not in rows" condition this would act on.
  useEffect(() => {
    if (
      selectedId === null ||
      isLoading ||
      selectedIndex > -1 ||
      edgeIntent !== null ||
      keepUrlParam
    ) {
      return;
    }
    setSelectedId(null, { replace: true });
  }, [
    selectedId,
    isLoading,
    selectedIndex,
    edgeIntent,
    keepUrlParam,
    setSelectedId,
  ]);

  // Resolve a pending crossing once the adjacent page has settled. Waiting on isFetching
  // matters because keepPreviousData leaves `rows` pointing at the previous page until
  // then, and selecting from it would land back where we started.
  useEffect(() => {
    if (edgeIntent === null) {
      return;
    }
    if (error) {
      // "Stay put" means going back to the page we came from, not just clearing the
      // intent: `page` already moved to the failed page when the crossing started, and
      // leaving it there desyncs the paginator from what's on screen. Hold the intent
      // until the source row is back in `rows` — dropping it early lets the stale-id
      // cleanup effect see an unmatched url and close the drawer. LedgerTab surfaces the
      // failure itself.
      if (page !== edgeIntent.fromPage) {
        onPageChange?.(edgeIntent.fromPage);
      }
      if (selectedIndex > -1) {
        setEdgeIntent(null);
      }
      return;
    }
    const rowsAreNew = rows !== edgeIntent.rowsAtIntent;
    // The intent ends when the url points at a row that exists in the rows on screen:
    // either the new page (crossing landed) or the page we came from (crossing abandoned
    // after a failure). It deliberately does not end when the url change is merely
    // requested — that state update lands a render before the router's does, and the
    // stale-id cleanup fires in the gap and wipes the selection.
    const backWhereWeStarted = page === edgeIntent.fromPage;
    if (selectedIndex > -1 && (rowsAreNew || backWhereWeStarted)) {
      setEdgeIntent(null);
      return;
    }
    if (isFetching || page !== edgeIntent.page || !rowsAreNew) {
      return;
    }
    if (rows.length === 0) {
      // failed crossing rather than leaving the intent stuck open forever.
      if (page !== edgeIntent.fromPage) {
        onPageChange?.(edgeIntent.fromPage);
      }
      return;
    }
    const row = edgeIntent.edge === "first" ? rows[0] : rows[rows.length - 1];
    // Replace so holding an arrow down does not fill the history with every row visited.
    setSelectedId(getRowId(row), { replace: true });
  }, [
    edgeIntent,
    error,
    isFetching,
    page,
    rows,
    selectedIndex,
    getRowId,
    onPageChange,
    setSelectedId,
  ]);

  const openPanel = useCallback(
    (row: T) => setSelectedId(getRowId(row)),
    [getRowId, setSelectedId]
  );

  const closePanel = useCallback(() => {
    setEdgeIntent(null);
    setSelectedId(null);
  }, [setSelectedId]);

  const crossTo = useCallback(
    (edge: Edge, nextPage: number) => {
      setEdgeIntent({
        edge,
        page: nextPage,
        rowsAtIntent: rows,
        fromPage: page,
      });
      onPageChange?.(nextPage);
    },
    [onPageChange, page, rows]
  );

  const resolvedRow = selectedIndex > -1 ? rows[selectedIndex] : null;
  // Hold the last real row so the panel stays open across a crossing. Without it the
  // selection is briefly null when the new page lands before the url catches up, and the
  // drawer closes under the user.
  const lastSelectedRef = useRef<T | null>(null);
  if (resolvedRow !== null) {
    lastSelectedRef.current = resolvedRow;
  }
  const selectedRow =
    resolvedRow ?? (edgeIntent !== null ? lastSelectedRef.current : null);

  const canCross = Boolean(onPageChange) && edgeIntent === null;
  const isLastOnPage = selectedIndex === rows.length - 1;
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  let onNext: (() => void) | undefined;
  if (selectedIndex > -1 && !isLastOnPage) {
    onNext = () =>
      setSelectedId(getRowId(rows[selectedIndex + 1]), {
        replace: true,
      });
  } else if (selectedIndex > -1 && isLastOnPage && hasNextPage && canCross) {
    onNext = () => crossTo("first", page + 1);
  }

  let onPrevious: (() => void) | undefined;
  if (selectedIndex > 0) {
    onPrevious = () =>
      setSelectedId(getRowId(rows[selectedIndex - 1]), {
        replace: true,
      });
  } else if (selectedIndex === 0 && hasPreviousPage && canCross) {
    onPrevious = () => crossTo("last", page - 1);
  }

  return {
    selectedRow,
    openPanel,
    closePanel,
    onNext,
    onPrevious,
    isCrossingPage: edgeIntent !== null,
  };
};
